(() => {
  if (window.MelCampaignDiscounts) return;

  const campaignCodes = ['WITCHNOV16', 'WITCHNOV28'];
  const routeRoot = () => window.Shopify?.routes?.root || '/';
  let queue = Promise.resolve();
  let state = '';
  let campaignKnown = false;
  let documentGeneration = 0;

  async function request(path, options = {}) {
    const controller = new AbortController();
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error('Offer check timed out'));
      }, 10000);
    });
    try {
      return await Promise.race([
        (async () => {
          const response = await fetch(`${routeRoot()}${path}`, {
            credentials: 'same-origin', cache: 'no-store', ...options,
            headers: { Accept: 'application/json', ...options.headers }, signal: controller.signal,
          });
          if (!response.ok) throw new Error('Offer check failed');
          return response.json();
        })(),
        timeout,
      ]);
    } finally {
      clearTimeout(timer);
    }
  }

  function codesFrom(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) throw new Error('Invalid cart response');
    const codes = new Map();
    cart.discount_codes.forEach(entry => {
      if (typeof entry?.code !== 'string' || !entry.code.trim()) throw new Error('Invalid discount response');
      const code = entry.code.trim();
      if (!codes.has(code.toUpperCase())) codes.set(code.toUpperCase(), code);
    });
    return codes;
  }

  async function update(activate, assertActiveDocument) {
    const cart = await request('cart.js');
    assertActiveDocument();
    const codes = codesFrom(cart);
    campaignKnown = campaignCodes.some(code => codes.has(code));
    if (!activate && !campaignKnown) return { cart, changed: false };
    if (campaignCodes.every(code => codes.has(code))) return { cart, changed: false };
    campaignCodes.forEach(code => { if (!codes.has(code)) codes.set(code, code); });
    // Keep both tiers, including an ineligible tier. Shopify chooses the price
    // again whenever quantities change, including changes made at checkout.
    // A lost response can still leave these codes on the cart.
    campaignKnown = true;
    try {
      const updated = await request('cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: [...codes.values()].join(',') }),
      });
      assertActiveDocument();
      const retained = codesFrom(updated);
      if ([...codes.keys()].some(code => !retained.has(code))) throw new Error('Offer was not retained');
      return { cart: updated, changed: true };
    } catch (error) {
      error.cartMayHaveChanged = true;
      throw error;
    }
  }

  function ensure({ activate = false } = {}) {
    // A non-activating cart check must never satisfy an activating sale request.
    const generation = documentGeneration;
    const assertActiveDocument = () => {
      if (generation !== documentGeneration) throw new Error('Please check the current bag after returning to this page');
      if (activate && state === 'leaving') throw new Error('Please wait for the page to reload');
    };
    const result = queue.then(async () => {
      assertActiveDocument();
      const updated = await update(activate, assertActiveDocument);
      assertActiveDocument();
      return updated;
    });
    queue = result.catch(() => {});
    return result;
  }

  window.MelCampaignDiscounts = { ensure };

  const purchaseForms = 'form[action$="/cart/add"], form[data-sale-form]';
  const cartScope = `cart-items, cart-drawer-items, #main-cart-footer, cart-drawer .drawer__footer, mel-cart-upsell, ${purchaseForms}`;
  const controls = 'input, select, textarea, button';
  const originalInert = new Map();
  let cartCheck;
  let checkRevision = 0;
  let bootstrapped = false;
  let needsRefresh = false;

  function makeInert(element) {
    if (!originalInert.has(element)) originalInert.set(element, element.hasAttribute('inert'));
    if (!element.hasAttribute('inert')) element.setAttribute('inert', '');
  }

  function syncControls() {
    if (!state) return;
    document.querySelectorAll(cartScope).forEach(scope => {
      scope.querySelectorAll(controls).forEach(control => {
        if (control.matches('[data-mel-campaign-retry]')) return;
        // Purchase and cart controllers own disabled. An independent inert lock
        // cannot restore their stale state when they recover after Back.
        makeInert(control);
      });
    });
    document.querySelectorAll('button[name="checkout"], .additional-checkout-buttons').forEach(makeInert);
    document.querySelectorAll(`#main-cart-footer, cart-drawer .drawer__footer, ${purchaseForms}`).forEach(scope => {
      let notice = scope.querySelector('[data-mel-campaign-notice]');
      if (!notice) {
        notice = document.createElement('div');
        notice.dataset.melCampaignNotice = '';
        notice.setAttribute('role', 'status');
        notice.setAttribute('aria-live', 'polite');
        const text = document.createElement('p');
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.dataset.melCampaignRetry = '';
        retry.textContent = 'Try again';
        retry.addEventListener('click', () => checkCart());
        notice.append(text, retry);
        scope.prepend(notice);
      }
      const message = state === 'error'
        ? 'We couldn’t refresh your offer. Please try again.'
        : 'Checking your offer…';
      if (notice.firstElementChild.textContent !== message) notice.firstElementChild.textContent = message;
      notice.querySelector('button').hidden = state !== 'error';
      notice.hidden = false;
    });
  }

  function setState(next) {
    state = next;
    if (next) {
      document.documentElement.dataset.melCampaignState = next;
      syncControls();
      return;
    }
    delete document.documentElement.dataset.melCampaignState;
    originalInert.forEach((inert, wrapper) => { if (!inert) wrapper.removeAttribute('inert'); });
    originalInert.clear();
    document.querySelectorAll('[data-mel-campaign-notice]').forEach(notice => { notice.hidden = true; });
  }

  function checkCart(fresh = false) {
    if (cartCheck && !fresh) return cartCheck;
    const revision = ++checkRevision;
    setState('pending');
    cartCheck = ensure().then(({ changed }) => {
      needsRefresh = needsRefresh || changed;
      if (revision !== checkRevision) return;
      if (needsRefresh) {
        setState('leaving');
        // Refresh prices without taking a product or campaign visitor to the bag
        // or adding a history entry that would redirect again after Back.
        window.location.reload();
      } else {
        setState('');
      }
    }).catch(error => {
      needsRefresh = needsRefresh || !!error.cartMayHaveChanged;
      if (revision !== checkRevision) return;
      // A read-only failure must not prevent ordinary shopping. Known campaign
      // carts and any uncertain discount write still need a successful check.
      setState(campaignKnown || needsRefresh ? 'error' : '');
    }).finally(() => { if (revision === checkRevision) cartCheck = null; });
    return cartCheck;
  }

  function initCart() {
    const marker = document.querySelector('[data-mel-campaign-cart-check]');
    if (bootstrapped || !marker) return;
    bootstrapped = true;
    campaignKnown = marker.hasAttribute('data-mel-campaign-known');
    checkCart();
  }

  document.addEventListener('submit', event => {
    if (!state) return;
    const form = event.target;
    if (form.matches('form') && (form.closest(cartScope) || new URL(form.action, window.location.href).pathname.replace(/\/$/, '').endsWith('/cart'))) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);
  document.addEventListener('click', event => {
    if (!state || !(event.target instanceof Element)) return;
    const control = event.target.closest('button, input, select, textarea, cart-remove-button, a');
    if (!control || control.matches('[data-mel-campaign-retry]')) return;
    const checkout = control.matches('[name="checkout"]') || (control.tagName === 'A' && new URL(control.href, window.location.href).pathname.endsWith('/checkout'));
    const cartControl = (control.tagName !== 'A' || control.closest('cart-remove-button')) && control.closest(cartScope);
    if (checkout || cartControl) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  function observeCart() {
    new MutationObserver(() => { initCart(); syncControls(); }).observe(document.body, {
      childList: true, subtree: true,
    });
    initCart();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observeCart, { once: true });
  else observeCart();
  document.addEventListener('shopify:section:load', initCart);
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    documentGeneration++;
    if (bootstrapped) checkCart(true);
  });
})();
